import React, { useEffect, useRef, useState } from "react";

import {
    View,
    Text,
    Image,
    Platform,
    TouchableOpacity,
    ScrollView,
    LayoutAnimation
} from "react-native";
import AppTheme from "../../utils/Theme";
import Images, { bundleImage } from "../../../specific/utils/Images";
import GameStyle from "../../styles/game/GameStyle";
import Activity from "../../models/Activity";
import { default as IcChevronLeft } from "../../assets/svg/icons/solid/chevron-left.svg";
import { default as IcChevronRight } from "../../assets/svg/icons/solid/chevron-right.svg";
import ActivityIndicator from "../../designSystem/ActivityIndicator/ActivityIndicator";
import MainStyle from "../../styles/game/MainStyle";
import { getLanguage } from "../../utils/Localization/Localization";

import BundleGameLabels from "../../data/jsons/local_db_game_labels.json";
import { getAppTarget } from "../../../specific/utils/Navigation/Host";
import SVGView from "../../utils/SvgView";
import { checkLocalUrl, getUrlToUse } from "../../utils/LocalStorage";
import { getAudioUrlForMediaId } from "../../../specific/services/AppWrite/AppWriteDatabase";
import { getLocalGameImages, getLocalGameLabels } from "../../../specific/utils/LocalStorage";

type blockElement = {
    l: string,
    a: string
}

interface GameCardsProps {
    activity: Activity,
    color: string,
    playASound: (sound: string, source?: string) => void,
    isMute: boolean,
    setTimer: (on: boolean) => void,
    pauseTimer: boolean,
    onImageGameEasterEggUnblocked?: () => void,
    expertMode?: boolean
}

let isGamePaused = true;
let indexAlreadyPicked: string[] = [];

const imageToHide: string[] = ["objet06BrosseADent", "objet15DrapeauCourse", "objet24JeuDeCarte", "objet34Regle"];

type USER_LEVEL = "BEGINNER" | "MEIDUM" | "EXPERT";

type gameRules = {
    number_of_cards: number,
    card_restriction: boolean,
    sound_on: boolean
}

const LEVEL_RULES: { [key: string]: gameRules } = {
    "BEGINNER": {
        number_of_cards: 5,
        card_restriction: true,
        sound_on: false
    },
    "MEIDUM": {
        number_of_cards: 8,
        card_restriction: true,
        sound_on: true
    },
    "EXPERT": {
        number_of_cards: 10,
        card_restriction: false,
        sound_on: true
    }
}

let numberOfCardTouch: number = 0;

const GameCards = (props: GameCardsProps) => {

    const appTheme: AppTheme = new AppTheme();
    const images: Images = new Images();
    const gameStyle: GameStyle = new GameStyle();
    const mainStyle: MainStyle = new MainStyle();

    let dbGameLabelsData = BundleGameLabels.data;

    const [viewDidAppear, setViewDidAppear] = useState<boolean>(false);
    const [blockLabels, setBlockLabels] = useState<any | undefined>(undefined);
    const [blockImages, setBlockImages] = useState<(number | bundleImage)[] | undefined>(undefined);
    const [onScreenCard, setOnScreenCard] = useState<number>(-1);
    const [refreshCards, setRefreshCards] = useState<number>(-1);
    const [userLevel, setUserLevel] = useState<USER_LEVEL>(((props.expertMode !== undefined) && (props.expertMode === true)) ? "EXPERT" : "BEGINNER");
    const [unblockExpertGame, setUnblockExpertGame] = useState<boolean>(false);
    const [histo, setHisto] = useState({}); // On va enregitrer l'historique des jeux pour éviter de reproposer trop souvent des labels identiques

    const { color, playASound, activity, isMute, setTimer, onImageGameEasterEggUnblocked } = props;

    const widthOfTheGame = Platform.OS === "web" ? appTheme.getFullAppWidth() : appTheme.getFullAppHeight();

    const scrollViewRef = useRef<ScrollView>(null);

    useEffect(() => {
        if (props.expertMode !== undefined) {
            if (props.expertMode === true) {
                setUserLevel("EXPERT");
            } else {
                setUserLevel("BEGINNER");
            }
        }
    }, [props.expertMode]);

    const onViewDidAppear = async () => {
        if (viewDidAppear === false) {
            if (Platform.OS !== "web") {
                const updatedGameLabel = await getLocalGameLabels();
                dbGameLabelsData = updatedGameLabel.data;
            }
            let showTheGame: boolean = true;
            if (activity.action.type === "game") {
                if (blockLabels === undefined) {
                    if (activity.action.auto !== undefined) {
                        launchAutomaticChangeForKeyAndTime(8000);
                    }
                    chooseBlockLabels();
                    showTheGame = false;
                }
            } else if (activity.action.type === "game_img") {
                if (blockImages === undefined) {
                    chooseBlockImages();
                    showTheGame = false;
                }
            }
            if (showTheGame === true) {
                setViewDidAppear(true);
            }
        }
    }

    useEffect(() => {
        if ((blockLabels !== undefined) && (onScreenCard === -1)) {
            setViewDidAppear(true);
            setOnScreenCard(0);
        } else {
            playCurrentLabel();
        }
    }, [blockLabels]);

    useEffect(() => {
        if (onScreenCard > -1) {
            if (activity.action.type === "game") {
                const blockLabelKeys = Object.keys(blockLabels);
                if (onScreenCard === blockLabelKeys.length) {
                    // On va lancer le jeu
                    if (blockLabelKeys.length < 4) {
                        setTimer(true);
                    }
                } else {
                    setTimer(false);
                    playLabel();
                }
            }
        }
    }, [onScreenCard]);

    useEffect(() => {
        if (refreshCards > -1) {
            if (props.pauseTimer === false) {
                chooseBlockLabels();
            }
            launchAutomaticChangeForKeyAndTime();
        }
    }, [refreshCards]);

    const launchAutomaticChangeForKeyAndTime = (delay?: number) => {
        if (activity.action.auto !== undefined) {
            let changeFrequency = activity.action.auto * 1000;
            if (delay !== undefined) {
                changeFrequency = changeFrequency + delay;
            }
            setTimeout(() => {
                // On lance une rafraichissement des cartes, pour prendre en compte le fait de savoir si le jeu est en pause ou non
                setRefreshCards(new Date().getTime());
            }, changeFrequency);
        }
    }

    const getBlockElement = (gameLabels: any[][], blockList: any) => {
        const aRandomNumber = Math.floor(Math.random() * gameLabels[blockList].data.length);
        let blockElement = gameLabels[blockList].data[aRandomNumber]
        const target = getAppTarget();
        if (target !== "public") {
            blockElement = { l: blockElement.l, a: getAudioUrlForMediaId({ media_id: blockList + "_" + aRandomNumber }) };
        }
        return blockElement;
    }

    // Jeu avec images
    useEffect(() => {
        if (blockImages !== undefined) {
            setViewDidAppear(true);
            setTimer(true);
        }
    }, [blockImages]);

    const chooseBlockImages = async () => {
        try {
            // On va regarder le nombre d'image à afficher
            let nbOfCards: number = 5;
            if ((LEVEL_RULES[userLevel] !== undefined) && (LEVEL_RULES[userLevel].number_of_cards !== undefined)) {
                nbOfCards = LEVEL_RULES[userLevel].number_of_cards;
            }
            if (activity.action.nb_of_cards !== undefined) {
                nbOfCards = activity.action.nb_of_cards;
            }
            // Il faut créer un tableau d'images aléatoires
            // On récupère dans ce tableau la liste des images à afficher
            let imagesToDisplay = [];
            const localImages = await getLocalGameImages();
            for (const anImageKeyIndex in activity.action.images) {
                if (Object.prototype.hasOwnProperty.call(activity.action.images, anImageKeyIndex)) {
                    const anImageKey: string = activity.action.images[anImageKeyIndex];
                    if ((localImages.data[anImageKey] !== undefined) && (localImages.data[anImageKey].data !== undefined)) {
                        imagesToDisplay = imagesToDisplay.concat(localImages.data[anImageKey].data);
                    }
                }
            }


            let refImagesBlocks: (number | bundleImage)[] = [];
            if (imagesToDisplay.length - indexAlreadyPicked.length <= nbOfCards) {
                indexAlreadyPicked = [];
            }
            while (refImagesBlocks.length < nbOfCards) {
                const randomIndex: number = Math.floor(Math.random() * imagesToDisplay.length);
                if (indexAlreadyPicked.indexOf(randomIndex) === -1) {
                    let imageRefToAdd: number | bundleImage = images[imagesToDisplay[randomIndex].app];
                    if (imageToHide.includes(imageRefToAdd) === false) {
                        indexAlreadyPicked.push(randomIndex);

                        if (imageRefToAdd === undefined) {
                            imageRefToAdd = { url: imagesToDisplay[randomIndex].url };
                        }
                        if (refImagesBlocks.indexOf(imageRefToAdd) === -1) {
                            refImagesBlocks.push(imageRefToAdd);
                        }
                    } else {
                        
                    }
                }
            }

            let imagesBlocks: (number | bundleImage)[] = [];
            while (refImagesBlocks.length > 0) {
                const randomIndex: number = Math.floor(Math.random() * refImagesBlocks.length);
                imagesBlocks.push(refImagesBlocks[randomIndex]);
                refImagesBlocks.splice(randomIndex, 1);
            }
            setBlockImages(imagesBlocks);
        } catch (error) {

        }

    }

    // Jeu classique
    const chooseBlockLabels = () => {
        const { action } = activity;
        const { blocks } = action;
        let newBlockLabels = {};
        let histoForGame = { ...histo };
        if (blocks !== undefined) {
            blocks.forEach(block => {
                if ((blockLabels === undefined) || (block.auto !== undefined)) {
                    const blockList = block.list + "_" + getLanguage();
                    if ((dbGameLabelsData[blockList] !== undefined) && (dbGameLabelsData[blockList].data !== undefined) && (dbGameLabelsData[blockList].data.length > 0)) {
                        let newElement = getBlockElement(dbGameLabelsData, blockList);
                        let histoForlist = histoForGame[blockList] !== undefined ? histoForGame[blockList] : [];
                        if (histoForlist.length === dbGameLabelsData[blockList].length) {
                            histoForlist.splice(0, 1);
                        }
                        while (histoForlist.includes(newElement.l) === true) {
                            newElement = getBlockElement(dbGameLabelsData, blockList);
                        }
                        newBlockLabels[block.key] = newElement;
                        histoForlist.push(newElement.l);
                        if ((histoForlist.length === 11) || (histoForlist.length === dbGameLabelsData[blockList].length - 1)) {
                            histoForlist.splice(0, 1);
                        }
                        histoForGame[blockList] = histoForlist;
                        setHisto(histoForGame);
                    } else {
                        canDisplayGames = false;
                    }
                } else if ((blockLabels !== undefined) && (blockLabels[block.key] !== undefined)) {
                    newBlockLabels[block.key] = blockLabels[block.key];
                }
            });
        }
        setBlockLabels(newBlockLabels);
    }

    const playLabel = async () => {
        playCurrentLabel();
        const blockLabelKeys = Object.keys(blockLabels);
        setTimeout(() => {
            if (blockLabelKeys.length < 4) {
                setOnScreenCard(onScreenCard + 1);
            } else {
                setTimer(true);
            }
        }, 4000);
    }

    const playCurrentLabel = async () => {
        let labelIndex = onScreenCard;
        const blockLabelKeys = Object.keys(blockLabels);
        if (labelIndex === blockLabelKeys.length) {
            labelIndex = labelIndex - 1;
        }
        if (blockLabelKeys[labelIndex] !== undefined) {
            if (isMute === false) {
                const labelToDisplay: blockElement = blockLabels[blockLabelKeys[labelIndex]];
                if (Platform.OS === "web") {
                    await playASound(labelToDisplay.a, "url");
                } else {
                    const stepKey = blockLabelKeys[labelIndex];
                    const stepKeysElements = stepKey.split("_");
                    const dirForOfflineFile = stepKeysElements[0] + "_" + getLanguage();
                    const localUrl = await getUrlToUse({ url: labelToDisplay.a, activityKey: dirForOfflineFile });
                    //const shortName = getShortNameForUrl(labelToDisplay.a);
                    //let localUrl = getPathForOriginal(shortName, "mp3", dirForOfflineFile);
                    //localUrl = checkLocalUrl(localUrl);
                    await playASound(localUrl, "url");
                }
            }
        }
    }

    useEffect(() => {
        if (unblockExpertGame === true) {
            if (onImageGameEasterEggUnblocked !== undefined) {
                onImageGameEasterEggUnblocked();
            }
            numberOfCardTouch = 0;
        }
    }, [unblockExpertGame]);

    const launchImageGameEasterEgg = () => {
        /*
        if (numberOfCardTouch < 20) {
            numberOfCardTouch++;
        } else {
            setUnblockExpertGame(true);
        }
            */
    }

    const launchEasterEgg = () => {

    }

    const onBlockScroll = (e) => {
        const { action } = activity;
        let numberOfBlocks: number = 2;
        let imageHeight: number = appTheme.getFullAppWidth() - appTheme.pixelPerfect(120); // On prend en compte la hauteur du Timer et du
        let imageWidth: number = imageHeight * 360 / 270;
        let widthForBlocks: number = imageWidth;
        if (action.type === "game") {
            const { blocks } = action;
            //L'espace total à utiliser est la hauteur du tel, moins 16à pixel de marge sur les côté
            //Jusqu'à 3 blocs on affiche tout sur un écran. Au dessus, on aura une scrollview horizontal donc on prend la valeur pour 2
            numberOfBlocks = blocks.length > 3 ? 2 : blocks.length;
            const separationMargin = appTheme.pixelPerfect(10);
            widthForBlocks = (widthOfTheGame - appTheme.pixelPerfect(100) - (numberOfBlocks - 1) * separationMargin) / numberOfBlocks;
            widthForBlocks = widthForBlocks > appTheme.pixelPerfect(200) ? appTheme.pixelPerfect(200) : widthForBlocks;
        }
        const widthForBlockContainer = widthForBlocks + appTheme.pixelPerfect(20);
        const presentX: number = onScreenCard * widthForBlockContainer;
        if (e.nativeEvent.contentOffset.x % e.nativeEvent.layoutMeasurement.width === 0) {
            if (presentX > e.nativeEvent.contentOffset.x) {
                moveCard(false);
            } else if (presentX < e.nativeEvent.contentOffset.x) {
                moveCard(true);
            }
        }
    }

    const moveCard = (forward: boolean) => {
        const { action } = activity;
        let newCard = onScreenCard;
        if (forward === true) {
            newCard = onScreenCard + 1;
        } else {
            newCard = onScreenCard - 1;
        }
        if (action.type === "game") {
            const { blocks } = action;
            //L'espace total à utiliser est la hauteur du tel, moins 16à pixel de marge sur les côté
            //Jusqu'à 3 blocs on affiche tout sur un écran. Au dessus, on aura une scrollview horizontal donc on prend la valeur pour 2
            const numberOfBlocks = blocks.length > 3 ? 2 : blocks.length;
            const separationMargin = appTheme.pixelPerfect(10);
            let widthForBlocks = (widthOfTheGame - appTheme.pixelPerfect(100) - (numberOfBlocks - 1) * separationMargin) / numberOfBlocks;
            widthForBlocks = widthForBlocks > appTheme.pixelPerfect(200) ? appTheme.pixelPerfect(200) : widthForBlocks;
            const widthForBlockContainer = widthForBlocks + appTheme.pixelPerfect(20);
            scrollViewRef.current?.scrollTo({ x: newCard * widthForBlockContainer, y: 0, animated: true });
        } else {
            LayoutAnimation.configureNext({
                duration: 1000,
                create: { duration: 100, type: 'linear', property: 'opacity' },
                update: { duration: 600, type: 'spring', springDamping: 0.9 },
                delete: { duration: 100, type: 'linear', property: 'opacity' },
            });
        }

        setOnScreenCard(newCard);
    }

    const getContent = () => {
        if (viewDidAppear === false) {
            return <View onLayout={onViewDidAppear} style={{ width: 1, height: 1 }} />
        }

        const { action } = activity;

        if ((action.type === "game_img") && (blockImages !== undefined)) {
            // Image dimensions => 360/270
            let imageHeight: number = appTheme.getFullAppWidth() - appTheme.pixelPerfect(100); // On prend en compte la hauteur du Timer et du
            if (Platform.OS === "web") {
                imageHeight = appTheme.getFullAppHeight() - appTheme.pixelPerfect(100);
                if (imageHeight > appTheme.pixelPerfect(360)) {
                    imageHeight = appTheme.pixelPerfect(360);
                }
            }
            let imageWidth: number = imageHeight * 360 / 270;
            const widthForBlockContainer = imageWidth + appTheme.pixelPerfect(20);
            let blocksView: React.JSX.Element[] = [];
            for (const aBlockIndex in blockImages) {
                if (Object.prototype.hasOwnProperty.call(blockImages, aBlockIndex)) {
                    let cardRotation: string = "0deg";
                    let cardLeftPosition: number = 0;
                    const cardRotations: string[] = ["0deg", "-7deg", "9deg", "-2deg", "6deg", "-8deg", "4deg", "-4deg", "9deg", "-9deg", "0deg"];
                    cardRotation = cardRotations[parseInt(aBlockIndex)];
                    if (parseInt(aBlockIndex) - 1 !== onScreenCard) {
                        if (parseInt(aBlockIndex) - 1 > onScreenCard) {
                            cardLeftPosition = Platform.OS === "web" ? appTheme.getFullScreenWidth() : appTheme.getFullScreenHeight();
                        }
                    }
                    const anImageSource: number | bundleImage = blockImages[aBlockIndex];
                    const aNewBlockView = <View style={{ transform: [{ rotate: cardRotation }], left: cardLeftPosition, position: "absolute", paddingBottom: appTheme.pixelPerfect(5), width: widthForBlockContainer, justifyContent: 'center', alignItems: 'center' }}>
                        <TouchableOpacity activeOpacity={1} onPress={launchImageGameEasterEgg} style={[{ height: imageHeight, width: imageWidth }]}>
                            <Image source={anImageSource} style={{ width: imageWidth, height: imageHeight, borderRadius: appTheme.pixelPerfect(20), borderWidth: appTheme.pixelPerfect(4), borderColor: appTheme.white }} resizeMode="cover" />
                        </TouchableOpacity>
                    </View>;
                    blocksView.push(aNewBlockView);
                }
            }
            const arrowButtonSize = appTheme.pixelPerfect(40);
            const leftNavigationButtons = onScreenCard > -1 ? <TouchableOpacity onPress={() => moveCard(false)} activeOpacity={1} style={{ justifyContent: 'center', alignItems: 'center', width: arrowButtonSize, height: arrowButtonSize, borderRadius: arrowButtonSize / 2, backgroundColor: appTheme.white }}>
                <SVGView Component={IcChevronLeft} size={appTheme.pixelPerfect(20)} color={color} />
            </TouchableOpacity> : <View style={{ width: arrowButtonSize }} />;
            const rightNavigationButtons = onScreenCard < (blockImages.length - 2) ? <TouchableOpacity onPress={() => moveCard(true)} activeOpacity={1} style={{ justifyContent: 'center', alignItems: 'center', width: arrowButtonSize, height: arrowButtonSize, borderRadius: arrowButtonSize / 2, backgroundColor: appTheme.white }}>
                <SVGView Component={IcChevronRight} size={appTheme.pixelPerfect(20)} color={color} />
            </TouchableOpacity> : <View style={{ width: arrowButtonSize }} />;
            return <View style={{ width: widthOfTheGame, flex: 1, justifyContent: "space-evenly", alignItems: 'center', flexDirection: "row" }}>
                {leftNavigationButtons}
                <View style={{ width: widthForBlockContainer }}>
                    <View style={[mainStyle.shadowed, { width: imageWidth, height: imageHeight }]}>
                        {blocksView}
                    </View>
                </View>

                {rightNavigationButtons}
            </View>;
        } else {
            const { blocks } = action;

            let blocksContainer = [];
            if (blocks !== undefined) {
                let blocksView = [];
                //On va calculer la largeur des blocs 
                //On va prendre un espace entre les blocs de 30
                const separationMargin = appTheme.pixelPerfect(10);
                //L'espace total à utiliser est la hauteur du tel, moins 16à pixel de marge sur les côté
                //Jusqu'à 3 blocs on affiche tout sur un écran. Au dessus, on aura une scrollview horizontal donc on prend la valeur pour 2
                const numberOfBlocks = blocks.length > 3 ? 2 : blocks.length;
                let widthForBlocks = (widthOfTheGame - appTheme.pixelPerfect(100) - (numberOfBlocks - 1) * separationMargin) / numberOfBlocks;
                widthForBlocks = widthForBlocks > appTheme.pixelPerfect(200) ? appTheme.pixelPerfect(200) : widthForBlocks;
                const widthForBlockContainer = widthForBlocks + appTheme.pixelPerfect(20);
                const horizontalMargin = blocks.length > 3 ? 0 : separationMargin / 2;
                for (const aBlockIndex in blocks) {
                    if (Object.prototype.hasOwnProperty.call(blocks, aBlockIndex)) {
                        const block = blocks[aBlockIndex];
                        let aBlockContent = blockLabels[block.key] !== undefined ? <Text style={{ textAlign: 'center', fontFamily: appTheme.primaryFont, fontSize: appTheme.pixelPerfectForFont(12), color: appTheme.darkBlue, paddingHorizontal: appTheme.pixelPerfect(5) }}>{blockLabels[block.key].l.toLocaleUpperCase()}</Text> : <ActivityIndicator color={appTheme.talk} />;
                        if (onScreenCard < parseInt(aBlockIndex)) {
                            aBlockContent = <ActivityIndicator color={appTheme.talk} />;
                        }
                        const aNewBlockView = <View style={{ width: widthForBlockContainer, justifyContent: 'center', alignItems: 'center', marginVertical: appTheme.pixelPerfect(20), marginHorizontal: horizontalMargin }}>
                            <TouchableOpacity activeOpacity={1} onPress={launchEasterEgg} style={[mainStyle.shadowed, { backgroundColor: appTheme.white, width: widthForBlocks }]}>
                                <View style={{ justifyContent: 'center', alignItems: 'center', backgroundColor: appTheme.darkBlue, width: widthForBlocks, height: appTheme.pixelPerfect(44) }}>
                                    <Text style={{ fontFamily: appTheme.primaryFont, fontSize: appTheme.pixelPerfectForFont(12), color }}>{block.title[getLanguage()].toLocaleUpperCase()}</Text>
                                </View>
                                <View style={{ justifyContent: 'center', alignItems: 'center', backgroundColor: appTheme.white, width: widthForBlocks, height: appTheme.pixelPerfect(76) }}>
                                    {aBlockContent}
                                </View>
                            </TouchableOpacity>
                        </View>;
                        blocksView.push(aNewBlockView);
                    }
                }
                if (blocks.length > 3) {
                    const arrowButtonSize = appTheme.pixelPerfect(40);
                    const leftNavigationButtons = onScreenCard > 0 ? <TouchableOpacity onPress={() => moveCard(false)} activeOpacity={1} style={{ justifyContent: 'center', alignItems: 'center', width: arrowButtonSize, height: arrowButtonSize, borderRadius: arrowButtonSize / 2, backgroundColor: appTheme.white }}>
                        <SVGView Component={IcChevronLeft} size={appTheme.pixelPerfect(20)} color={color} />
                    </TouchableOpacity> : <View style={{ width: arrowButtonSize }} />;
                    const rightNavigationButtons = onScreenCard < (blocks.length - 1) ? <TouchableOpacity onPress={() => moveCard(true)} activeOpacity={1} style={{ justifyContent: 'center', alignItems: 'center', width: arrowButtonSize, height: arrowButtonSize, borderRadius: arrowButtonSize / 2, backgroundColor: appTheme.white }}>
                        <SVGView Component={IcChevronRight} size={appTheme.pixelPerfect(20)} color={color} />
                    </TouchableOpacity> : <View style={{ width: arrowButtonSize }} />;
                    blocksContainer = <View style={{ width: widthOfTheGame, justifyContent: "space-evenly", alignItems: 'center', flexDirection: "row" }}>
                        {leftNavigationButtons}
                        <View style={{ width: widthForBlockContainer }}>
                            <ScrollView
                                style={{ width: widthForBlockContainer }}
                                ref={scrollViewRef}
                                onScroll={onBlockScroll}
                                pagingEnabled={true}
                                showsHorizontalScrollIndicator={false}
                                horizontal={true}>
                                <View style={{ flexDirection: 'row' }}>
                                    {blocksView}
                                </View>
                            </ScrollView>
                        </View>

                        {rightNavigationButtons}
                    </View>;
                } else {
                    blocksContainer = <View style={{ flexDirection: 'row' }}>{blocksView}</View>;
                }
            }

            return blocksContainer;
        }
    }

    return getContent();
}

export default GameCards;